import { useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { Header } from '../components/layout/Header.jsx'
import { TabBar } from '../components/layout/TabBar.jsx'
import { Footer } from '../components/layout/Footer.jsx'
import { FeedControls } from '../components/feed/FeedControls.jsx'
import { BillCard } from '../components/feed/BillCard.jsx'
import { CandidateCard } from '../components/feed/CandidateCard.jsx'
import { WelcomeCard } from '../components/feed/WelcomeCard.jsx'
import { AnalyticsPane } from '../components/analytics/AnalyticsPane.jsx'
import { ProposePane } from '../components/propose/ProposePane.jsx'
import { JurisdictionPane } from '../components/jurisdiction/JurisdictionPane.jsx'
import { SignInModal } from '../components/auth/SignInModal.jsx'
import { useNip05Verification } from '../hooks/useNip05Verification.js'
import { useServerSnapshots } from '../hooks/useServerSnapshots.js'
import { AccountModal } from '../components/account/AccountModal.jsx'
import { useNostr } from '../hooks/useNostr.js'
import { useNostrLogin } from '../hooks/useNostrLogin.js'
import { useFeed } from '../hooks/useFeed.js'
import { usePullToRefresh } from '../hooks/usePullToRefresh.js'
import { buildActivityIndex } from '../utils/pollActivity.js'
import { effectiveActivityMs, timeframeCutoffMs } from '../utils/activityDate.js'
import { MovingStrip } from '../components/feed/MovingStrip.jsx'
import { loadLastSeen, markSeenNow } from '../utils/visitTracker.js'
import { CatchUpCard } from '../components/feed/CatchUpCard.jsx'
import { HistoryPane } from '../components/history/HistoryPane.jsx'
import { loadTopicSubs, toggleTopicSub } from '../utils/topicSubs.js'
import { useProfile } from '../hooks/useProfile.js'
import {
  loadJurisdiction,
  clearJurisdiction,
  jurisdictionTagValue,
  loadJurisdictionForPubkey,
  saveJurisdictionForPubkey,
  saveJurisdiction,
} from '../utils/jurisdiction.js'
import { buildVoteEvent, buildQuoteEvent, rememberMyEvent } from '../utils/nostr.js'
import { DISCLAIMER_TEXT } from '../utils/constants.js'
import { trackFirstVote } from '../utils/analytics.js'
import { loadQuillState, recordVoteNow, checkMissedDay, emptyQuillState } from '../utils/quillStreak.js'
import {
  incrementEphemeralVoteCount,
  shouldShowUpgradePrompt,
  snoozeUpgradePrompt,
  loadEphemeralVoteCount,
} from '../utils/ephemeralVote.js'
import { UpgradePrompt } from '../components/feed/UpgradePrompt.jsx'
import { StreakCelebration } from '../components/feed/StreakCelebration.jsx'
import { writePrefsFile, loadDriveToken } from '../utils/googleAuth.js'

// UX44: push the current jurisdiction up to Drive. Any failure is
// swallowed; the next sign-in has the local value and will upload
// again with a fresh token.
function syncJurisdictionToDrive(jurisdiction) {
  const drv = loadDriveToken()
  if (!drv) return
  writePrefsFile({ drv, prefs: { jurisdiction } }).catch(() => { /* noop */ })
}

const ALL_TYPES = new Set(['federal', 'state', 'city', 'candidate', 'user'])

export default function Home() {
  const [tab, setTab] = useState('feed')
  const [scope, setScope] = useState('mine')
  // UX53: search query mirrors to URL ?q= so searches are shareable.
  // Initial value reads the query param on mount; subsequent updates
  // sync via the setQuery wrapper below.
  const [query, setQueryRaw] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('q') || '' }
    catch { return '' }
  })
  const setQuery = (next) => {
    setQueryRaw(next)
    try {
      const url = new URL(window.location.href)
      if (next) url.searchParams.set('q', next)
      else url.searchParams.delete('q')
      // replaceState — don't push every keystroke into history.
      window.history.replaceState({}, '', url)
    } catch { /* noop */ }
  }
  const [activeTypes, setActiveTypes] = useState(new Set(ALL_TYPES))
  // UX55: topic subscriptions state. Mirrors loadTopicSubs for the
  // signed-in pubkey; reloaded in the auth.pubkey useEffect below.
  const [subscribedTopics, setSubscribedTopics] = useState([])
  const [followedOnly, setFollowedOnly] = useState(false)
  // UX67: temporal narrowing — defaults to "all time" so the feed
  // behaves exactly as before until the user picks a window.
  const [timeframe, setTimeframe] = useState('all')
  const [topic, setTopic] = useState(() => {
    try { return localStorage.getItem('verdant_feed_topic') || '' }
    catch { return '' }
  })
  const [jurisdiction, setJurisdiction] = useState(() => loadJurisdiction())
  const [authModal, setAuthModal] = useState(null) // null | 'choose' | 'unlock' | 'google-unlock'
  // UX30: when the user clicks the Header "Create account" button we want
  // the modal to land on the Create view regardless of NIP-07 detection.
  // null = fall back to SignInModal's own default-tab logic.
  const [authModalTab, setAuthModalTab] = useState(null)
  const [accountOpen, setAccountOpen] = useState(false)
  // UX36a / UX49: quill streak state + the one-shot toast when the
  // user either finds or loses a quill. State is PER-PUBKEY — the
  // keyed effect below loads it (and runs the mount-time miss check)
  // whenever auth.pubkey changes, so signing out clears it and
  // signing in restores the right one.
  const [quillState, setQuillState] = useState(() => emptyQuillState())
  const [quillToast, setQuillToast] = useState(null) // null | {type:'found'} | {type:'lost', daysHeld}
  // UX43: streak-milestone celebration (7 / 30 / 100 / 365). Fires at
  // most once per milestone; persisted in quillStreak localStorage.
  const [streakCelebration, setStreakCelebration] = useState(null) // null | { days }
  // UX41: when an ephemeral user hits the vote threshold (or the
  // snooze window elapses), set this to render the upgrade prompt.
  // null when no prompt should be visible.
  const [upgradePrompt, setUpgradePrompt] = useState(null) // null | { voteCount }
  // When a user clicks "Detailed metrics →" on a feed card we set this and
  // route to the Analytics tab; AnalyticsPane reads it as initialPollId.
  // Cleared when user navigates to Analytics via the TabBar (so the tab
  // click defaults to overview, not whatever poll was last drilled into).
  const [analyticsPollId, setAnalyticsPollId] = useState('')

  const userJurisdictionTag = useMemo(
    () => jurisdictionTagValue(jurisdiction),
    [jurisdiction]
  )

  const nostr = useNostr()
  const auth = useNostrLogin()
  const { items, tallies, loading, error, voteIndex, addLocalEvent, addLocalVote, refresh } = useFeed({
    pool: nostr.pool,
    subscribe: nostr.subscribe,
    userJurisdiction: userJurisdictionTag,
    userPubkey: auth.pubkey,
  })
  const { profile, loading: profileLoading, updateProfile } = useProfile({
    subscribe: nostr.subscribe,
    pubkey: auth.pubkey,
    sign: auth.sign,
    publish: nostr.publish,
  })

  // UX43: pull-to-refresh. Window-level touch gesture, arms only when
  // the feed is scrolled to the top. onRefresh re-subscribes to the
  // bulk #ThePeopleVote filter via useFeed's refresh(). Existing items
  // are kept across the refresh so there's no flash.
  const { pullY, refreshing: feedRefreshing, triggered: pullTriggered, threshold: pullThreshold } =
    usePullToRefresh({ onRefresh: refresh })

  // UX45: social-proof activity index. Rebuilt whenever voteIndex
  // changes — a thin O(votes) pass, cheaper than letting every card
  // scan its own slice on every render.
  const activityIndex = useMemo(() => buildActivityIndex(voteIndex), [voteIndex])

  // UX46: catch-up card on return visits. prevLastSeenRef captures
  // the stored timestamp BEFORE we overwrite it to "now" on mount, so
  // the current session can still compute "polls newer than my prior
  // visit." markSeenNow() runs once on mount regardless of what we
  // show, so next return reads this visit as the baseline.
  const prevLastSeenRef = useRef(null)
  const [catchUpCount, setCatchUpCount] = useState(0)
  const [catchUpDismissed, setCatchUpDismissed] = useState(false)
  useEffect(() => {
    prevLastSeenRef.current = loadLastSeen()
    markSeenNow()
  }, [])
  useEffect(() => {
    if (catchUpDismissed) return
    if (loading) return
    const since = prevLastSeenRef.current
    if (since === null) return  // first-ever visit, nothing to compare to
    const n = items.filter((it) => (it.postedAt || 0) > since).length
    if (n > 0) setCatchUpCount(n)
  }, [items, loading, catchUpDismissed])

  // UX29 Phase 1.5: route locked Google users to the 'google-unlock'
  // confirm-and-OAuth view instead of the passphrase one. Reads `auth`
  // so must be declared after useNostrLogin() above.
  const unlockMode = () => auth.loginType === 'google' ? 'google-unlock' : 'unlock'
  // T16: NIP-05 verification state for the signed-in user, lifted here
  // so Header and AccountModal (both rendering Nip05Display for the
  // same user) share one check instead of each running the hook. Reads
  // `auth` and `profile` so must be declared after useProfile() above.
  const { verified: nip05Verified } = useNip05Verification({
    pubkey: auth.pubkey,
    nip05: profile?.nip05,
  })

  // Banner shown when the user just posted a poll, so the success state
  // survives the tab switch out of ProposePane.
  const [postBanner, setPostBanner] = useState(null)

  // If the user has an encrypted key on disk, surface the unlock modal as
  // soon as the app loads so they can sign votes immediately. One-shot:
  // if the user dismisses (Esc / backdrop / X), don't re-open automatically.
  // They can re-trigger via the header sign-in button or any vote click.
  //
  // Gated on !auth.restoring so the modal doesn't flash open during the
  // mount-time sessionStorage restore that's about to silently unlock the
  // account on a refresh (T08).
  const autoOpenedUnlockRef = useRef(false)
  useEffect(() => {
    if (auth.locked && !auth.restoring && authModal === null && !autoOpenedUnlockRef.current) {
      autoOpenedUnlockRef.current = true
      setAuthModal(unlockMode())
    }
    if (!auth.locked) autoOpenedUnlockRef.current = false
  }, [auth.locked, auth.restoring, authModal])

  // Console diagnostic: window.verdant gives easy access to runtime state.
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.verdant = {
      pubkey: auth.pubkey,
      npub: auth.npub,
      profile,
      profileLoading,
      items,
      tallies,
      voteIndex,
      jurisdiction,
      myCachedEvents: () => {
        try { return JSON.parse(localStorage.getItem('verdant_my_events') || '[]') }
        catch { return [] }
      },
    }
  }, [auth.pubkey, auth.npub, profile, profileLoading, items, tallies, voteIndex, jurisdiction])

  // Derive the signed-in user's vote map purely from voteIndex (relay-
  // confirmed, plus optimistic-local-insert via addLocalVote in
  // handleVote, plus localStorage-cached own events hydrated by useFeed
  // on mount). No separate per-pubkey cache, no merge effect — the
  // memo can't leak across accounts because it loops only for the
  // current pubkey and voteIndex itself is keyed by signer pubkey.
  // When signed out (pubkey=null) the map is empty.
  const userVotes = useMemo(() => {
    if (!auth.pubkey) return {}
    const out = {}
    for (const pollId of Object.keys(voteIndex)) {
      const mine = voteIndex[pollId][auth.pubkey]
      if (mine?.choice) out[pollId] = mine.choice
    }
    return out
  }, [voteIndex, auth.pubkey])

  // Mark each item as resident vs non-resident based on user jurisdiction.
  // UX15/UX34: server-side snapshots. Supplies bill_actions timelines
  // for the SentimentTrend annotations and the daily poll-of-the-day
  // pick. Empty on failure, so the UI gracefully falls back to the
  // existing billActions: [] default and skips the hero card.
  const { pollOfTheDay, pollOfTheDayKind, billOfTheMoment, polls: serverPolls } = useServerSnapshots()

  // UX36a / UX49: whenever the signed-in pubkey changes, (re)load the
  // quill state and run the mount-time miss check. Signed-out leaves
  // the local badge empty.
  useEffect(() => {
    if (!auth.pubkey) {
      setQuillState(emptyQuillState())
      return
    }
    const lost = checkMissedDay(auth.pubkey)
    const current = loadQuillState(auth.pubkey)
    setQuillState(current)
    if (lost) {
      // UX47: canSave is set when the loss happened today, meaning a
      // vote right now can rescue the prior streak. Toast copy adapts.
      setQuillToast({
        type: 'lost',
        daysHeld: lost.daysHeld,
        canSave: !!lost.canSave,
        streakWas: lost.streakWas,
      })
    }

    // UX49: restore the signed-in user's saved jurisdiction. The
    // local pill was cleared on sign-out so the next person on this
    // device starts blank; here we re-populate it from the pubkey's
    // saved record. Google users also get a Drive overlay via UX44's
    // finishSignIn — that path still runs and is a no-op when the
    // local shadow is already current.
    //
    // UX64: migration safety net. Users who set their ZIP BEFORE
    // UX49 shipped only ever had the global LS entry populated —
    // handleJurisdictionChange wasn't yet writing per-pubkey. First
    // logout cleared the global, next login found nothing per-pubkey,
    // so the ZIP looked like it reset. If the per-pubkey entry is
    // empty but a global one exists, migrate it over.
    let savedJ = loadJurisdictionForPubkey(auth.pubkey)
    if (!savedJ) {
      const globalJ = loadJurisdiction()
      if (globalJ) {
        saveJurisdictionForPubkey(auth.pubkey, globalJ)
        savedJ = globalJ
      }
    }
    if (savedJ) {
      setJurisdiction(savedJ)
      // Keep the global pill in sync so the Header reads the same
      // value even before any explicit change.
      try { saveJurisdiction(savedJ) } catch { /* noop */ }
    }

    // UX55: load topic subscriptions for this pubkey.
    setSubscribedTopics(loadTopicSubs(auth.pubkey))
  }, [auth.pubkey])

  // Auto-dismiss the quill toast after a short pause. User can also
  // click × to dismiss it manually.
  useEffect(() => {
    if (!quillToast) return
    const t = setTimeout(() => setQuillToast(null), 6000)
    return () => clearTimeout(t)
  }, [quillToast])

  // Sign-out helper. Wraps auth.logout to also drop the saved
  // jurisdiction so the next person on this device starts from a
  // blank location pill instead of inheriting the prior user's
  // district / ZIP. (Jurisdiction is per-browser, not per-pubkey;
  // without this, logout keeps it populated.)
  const handleLogout = () => {
    auth.logout()
    clearJurisdiction()
    setJurisdiction(null)
  }

  // UX29 Phase 1.5 fix: the Welcome back modal (authModal ===
  // 'google-unlock') launches a Google OAuth redirect, so nothing in
  // the SignInModal's own click handler can call onClose on success —
  // the browser has already navigated away. When the user returns and
  // the OAuth-callback useEffect flips auth.isLoggedIn to true, close
  // the modal here. Scoped to 'google-unlock' only so the create /
  // import / BackupView flows aren't dismissed prematurely.
  useEffect(() => {
    if (auth.isLoggedIn && authModal === 'google-unlock') {
      setAuthModal(null)
      setAuthModalTab(null)
    }
  }, [auth.isLoggedIn, authModal])

  const itemsWithResidency = useMemo(() => {
    return items.map((it) => {
      const base = {
        ...it,
        resident: userJurisdictionTag
          ? userJurisdictionTag.startsWith(it.jurisdiction) ||
            it.jurisdiction.startsWith(userJurisdictionTag)
          : null,
      }
      // Overlay server-sourced bill actions for bill items. The feed
      // event parser sets billActions=[] by default; the server map
      // may supply a non-empty array for the SentimentTrend
      // annotations.
      if (it.kind === 'bill') {
        const srv = serverPolls[it.id]
        if (srv?.bill_actions?.length) {
          base.billActions = srv.bill_actions
        }
      }
      return base
    })
  }, [items, userJurisdictionTag, serverPolls])

  // Topics actually present in the loaded feed — drives the chip row
  // so we don't render 32 always-empty CATEGORIES pills.
  const presentTopics = useMemo(() => {
    const set = new Set()
    for (const item of items) {
      for (const c of item.categories || []) set.add(c)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [items])

  useEffect(() => {
    try { localStorage.setItem('verdant_feed_topic', topic) }
    catch { /* noop */ }
  }, [topic])

  // UX67: timeframe cutoff — items whose effective activity date is
  // older than the cutoff are dropped. Pulse polls bypass the filter
  // because their activity date returns 0 (they're always "today's
  // conversation" regardless of calendar date).
  const timeframeCutoff = useMemo(() => timeframeCutoffMs(timeframe), [timeframe])

  // Filter for the visible feed.
  const visibleItems = useMemo(() => {
    const topicLower = topic.toLowerCase()
    const sorted = itemsWithResidency.slice().sort((a, b) => {
      // UX67: smart default sort. Pulse polls pin to very top via
      // their own hero path; here we weight recently-active bills
      // above dormant ones. Items with activity in the last 7 days
      // bubble up before older/static ones; within each bucket,
      // activity date desc.
      const am = effectiveActivityMs(a)
      const bm = effectiveActivityMs(b)
      const now = Date.now()
      const WEEK = 7 * 24 * 60 * 60 * 1000
      const aFresh = am && (now - am) < WEEK ? 1 : 0
      const bFresh = bm && (now - bm) < WEEK ? 1 : 0
      if (aFresh !== bFresh) return bFresh - aFresh
      if (am !== bm) return bm - am
      return (b.postedAt?.getTime?.() || 0) - (a.postedAt?.getTime?.() || 0)
    })
    return sorted.filter((item) => {
      // type filter
      const typeKey = item.kind === 'candidate' ? 'candidate' : item.level
      if (!activeTypes.has(typeKey)) return false

      // scope filter
      if (scope === 'mine') {
        if (item.resident === false) return false
      }

      // topic filter
      if (topic) {
        const cats = (item.categories || []).map((c) => c.toLowerCase())
        if (!cats.includes(topicLower)) return false
      }

      // UX55: followed-topics-only filter. Only applies when the user
      // has at least one subscription, is signed in, and has flipped
      // the toggle. Takes the union of their subs vs the poll's
      // categories (case-insensitive).
      if (followedOnly && subscribedTopics.length > 0) {
        const cats = (item.categories || []).map((c) => c.toLowerCase())
        const hit = cats.some((c) => subscribedTopics.includes(c))
        if (!hit) return false
      }

      // UX53: search haystack. Topic categories were missing before —
      // searching "immigration" wouldn't match a bill tagged
      // Topic: Immigration unless the word also appeared in the title
      // or summary. Added categories + candidate names.
      if (query) {
        const q = query.toLowerCase()
        const haystack = [
          item.title || '',
          item.plainSummary || '',
          item.billId || '',
          item.levelLabel || '',
          item.jurisdiction || '',
          Array.isArray(item.categories) ? item.categories.join(' ') : '',
          Array.isArray(item.candidates)
            ? item.candidates.map((c) => `${c.name || ''} ${c.party || ''}`).join(' ')
            : '',
        ].join(' ').toLowerCase()
        if (!haystack.includes(q)) return false
      }

      // UX67: timeframe filter. Pulse polls bypass (effectiveActivityMs
      // returns 0 so they'd otherwise fail every cutoff). Items with
      // no activity data at all pass through on "all" and get filtered
      // out on narrower windows — honest with the data uncertainty.
      if (timeframeCutoff !== null) {
        if (item.botName === 'pulse') return true
        const ms = effectiveActivityMs(item)
        if (!ms) return false
        if (ms < timeframeCutoff) return false
      }
      return true
    })
  }, [itemsWithResidency, activeTypes, scope, query, topic, followedOnly, subscribedTopics, timeframeCutoff])

  // UX34: find the poll-of-the-day item in the visible list and pull
  // it out so it can render as a hero card above the regular feed.
  // Silently falls back to no hero (just the normal list) when the
  // server pick isn't visible to this user — jurisdiction scope,
  // topic filter, search, or type-filter might exclude it. The
  // regular feed stays unchanged in that case.
  // UX57 dual-hero resolver.
  // - pulseHero: today's "⭐ Today's pulse" card. Falls back to the
  //   legislative hero on days the pulse bot didn't run (see
  //   snapshot_job's own fallback logic).
  // - billHero: "🔥 Bill of the moment" card, only present when the
  //   legislative scorer winner cleared POTD_BILL_FLOOR on the server.
  // Both are joined to visibleItems so a card the user filtered out
  // (jurisdiction / topic / search) doesn't surface as a hero.
  const pulseHero = useMemo(() => {
    if (!pollOfTheDay) return null
    return visibleItems.find((it) => it.id === pollOfTheDay) || null
  }, [visibleItems, pollOfTheDay])
  const billHero = useMemo(() => {
    if (!billOfTheMoment) return null
    // Don't double-render if the pulse slot already holds this id.
    if (pulseHero && pulseHero.id === billOfTheMoment) return null
    return visibleItems.find((it) => it.id === billOfTheMoment) || null
  }, [visibleItems, billOfTheMoment, pulseHero])

  const visibleItemsSansHero = useMemo(() => {
    const excludeIds = new Set()
    if (pulseHero) excludeIds.add(pulseHero.id)
    if (billHero) excludeIds.add(billHero.id)
    if (excludeIds.size === 0) return visibleItems
    return visibleItems.filter((it) => !excludeIds.has(it.id))
  }, [visibleItems, pulseHero, billHero])

  const toggleType = (id) => {
    setActiveTypes((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleVote = async (item, choice) => {
    // UX41: if the user has an encrypted account that needs unlocking,
    // we still route to the Unlock modal — they're not signed out so
    // much as locked. For every other signed-out state (no account
    // yet, or an active ephemeral key), we proceed under an ephemeral
    // identity. beginEphemeral() is idempotent so we can call it even
    // if a key is already in memory.
    if (auth.locked) {
      setAuthModal(unlockMode())
      return
    }
    const isNewEphemeral = !auth.isLoggedIn
    if (isNewEphemeral) {
      const pk = auth.beginEphemeral()
      if (!pk) return
    }
    const wasEphemeral = isNewEphemeral || auth.loginType === 'ephemeral'

    // No-op if the user re-clicks their existing choice — don't spam relays
    // with a duplicate event. The current vote is already what they want.
    const existing = voteIndex[item.id]?.[auth.pubkey]
    if (existing?.choice?.toLowerCase() === choice.toLowerCase()) return
    if (!existing && userVotes[item.id]?.toLowerCase() === choice.toLowerCase()) return

    try {
      const template = buildVoteEvent({
        pollEventId: item.id,
        pollAuthor: item.pubkey,
        choice,
        userJurisdiction: userJurisdictionTag,
        poll: item,
      })
      const signed = await auth.sign(template)
      const result = await nostr.publish(signed)
      console.info('[Verdant] published vote', {
        id: signed.id, choice, pollId: item.id,
        accepted: result.accepted, total: result.total,
      })

      // UX43: short haptic buzz on supporting devices. navigator.vibrate
      // is quietly ignored on desktop and iOS; Android browsers surface a
      // single tap of feedback. Non-blocking — no await, no error path.
      try { navigator?.vibrate?.(20) } catch { /* noop */ }

      // Optimistic: insert into the local index immediately so the bar
      // updates without waiting for the relay echo. The latest-wins logic
      // in useFeed will replace any prior vote from this pubkey.
      addLocalVote(signed)
      // Persist to localStorage so the vote survives reloads even if no
      // relay echoes it back through the subscription.
      rememberMyEvent(signed)
      trackFirstVote({ pubkey: auth.pubkey, level: item.level })

      // UX36a / UX43: streak + quill accounting. Fires only on vote
      // success (so interrupted / failed publishes don't advance the
      // streak). Notification shape:
      //   { type: 'found' }                    → quill-found toast
      //   { type: 'milestone', days: 7|30|100 } → center celebration
      // UX49: quill state is per-pubkey, keyed by whoever's signed in.
      // For ephemeral voters this is the just-generated ephemeral key,
      // which later carries over on Google-link (UX41).
      const quill = recordVoteNow(auth.pubkey)
      setQuillState(quill.state)
      if (quill.notification?.type === 'found') {
        setQuillToast({ type: 'found' })
      } else if (quill.notification?.type === 'milestone') {
        setStreakCelebration({ days: quill.notification.days })
      } else if (quill.notification?.type === 'saved') {
        // UX47: the lost-streak grace was rescued. Reuse the
        // celebration overlay with a special days=0 variant marked
        // via a 'saved' flag — StreakCelebration renders rescue copy.
        setStreakCelebration({
          days: quill.notification.streak,
          saved: true,
          daysHeld: quill.notification.daysHeld,
        })
      }

      // UX46: once the user votes, the catch-up strip has done its
      // job (they engaged). Dismiss it so it doesn't linger over the
      // post-vote reveal.
      setCatchUpDismissed(true)
      setCatchUpCount(0)

      // UX41: bump the ephemeral vote counter when the current session
      // is anonymous. shouldShowUpgradePrompt honors the snooze window
      // so dismissing doesn't re-pop on the very next vote.
      if (wasEphemeral) {
        const count = incrementEphemeralVoteCount()
        if (shouldShowUpgradePrompt(count)) {
          setUpgradePrompt({ voteCount: count })
        }
      }

    } catch (err) {
      console.error('[Verdant] vote failed', err)
      Sentry.withScope((s) => {
        s.setTag('op', 'vote_publish')
        s.setExtra('pollId', item.id)
        s.setExtra('choice', choice)
        Sentry.captureException(err)
      })
      alert(err?.message || 'Failed to publish vote.')
    }
  }

  const handleJurisdictionChange = (j) => {
    setJurisdiction(j)
    // UX49: shadow the choice per-pubkey so sign-out / sign-in on the
    // same device restores it without needing a Drive round-trip.
    if (auth.pubkey) {
      saveJurisdictionForPubkey(auth.pubkey, j)
    }
    // UX44: Google users get Drive-synced prefs. Fire-and-forget — if
    // the token is expired or the write fails, the next sign-in will
    // pick up the local value when it uploads with a fresh token.
    if (auth.loginType === 'google') {
      syncJurisdictionToDrive(j)
    }
    // After a save, route to the feed scoped to the new jurisdiction.
    // A clear (j === null) leaves the user on the Jurisdiction tab.
    if (j) {
      setTab('feed')
      setScope('mine')
    }
  }

  // Tab navigation. When the user clicks the Analytics tab directly we
  // clear analyticsPollId so the tab defaults to the overview view; if
  // they came in via a card's "Detailed metrics →" link we left it set.
  const handleTabChange = (tabId) => {
    if (tabId === 'analytics') setAnalyticsPollId('')
    setTab(tabId)
  }

  const handleViewMetrics = (pollId) => {
    setAnalyticsPollId(pollId)
    setTab('analytics')
  }

  // UX12: 24h-dismissable banner that nudges users without a jurisdiction
  // toward the location picker. Suppressed when WelcomeCard is showing
  // (UX07 already includes a "Set your location" CTA).
  const [jurisdictionBannerDismissedAt, setJurisdictionBannerDismissedAt] = useState(() => {
    try { return Number(localStorage.getItem('verdant_jurisdiction_prompt_dismissed_at') || 0) }
    catch { return 0 }
  })
  const welcomeDismissed = (() => {
    try { return localStorage.getItem('verdant_welcome_dismissed') === '1' }
    catch { return false }
  })()
  const showJurisdictionBanner =
    !jurisdiction &&
    (auth.pubkey || welcomeDismissed) &&
    Date.now() - jurisdictionBannerDismissedAt > 24 * 60 * 60 * 1000
  const dismissJurisdictionBanner = () => {
    const now = Date.now()
    try { localStorage.setItem('verdant_jurisdiction_prompt_dismissed_at', String(now)) }
    catch { /* noop */ }
    setJurisdictionBannerDismissedAt(now)
  }

  const handleShareQuote = async ({ item, title, shareUrl, comment, content }) => {
    if (!auth.isLoggedIn) {
      setAuthModal(auth.locked ? unlockMode() : 'choose')
      return false
    }
    try {
      const template = buildQuoteEvent({
        pollEventId: item.id,
        pollAuthor: item.pubkey,
        title,
        shareUrl,
        comment,
        content,
      })
      const signed = await auth.sign(template)
      const result = await nostr.publish(signed)
      console.info('[Verdant] published quote', {
        id: signed.id, pollId: item.id,
        accepted: result.accepted, total: result.total,
      })
      rememberMyEvent(signed)
      return true
    } catch (err) {
      console.error('[Verdant] quote failed', err)
      alert(err?.message || 'Failed to publish quote.')
      return false
    }
  }

  return (
    <div className="app-shell">
      {/* UX43: pull-to-refresh strip. Lives above the Header, grows
          with the drag, and flips chevron → spinner at the trigger
          threshold. Hidden entirely when pullY === 0. */}
      {pullY > 0 && (
        <div
          className={`ptr-strip${feedRefreshing ? ' ptr-refreshing' : ''}${pullTriggered ? ' ptr-triggered' : ''}`}
          style={{ height: `${pullY}px` }}
          aria-hidden="true"
        >
          <div className="ptr-indicator">
            {feedRefreshing ? (
              <span className="ptr-spinner" />
            ) : (
              <span
                className="ptr-chevron"
                style={{ transform: `rotate(${Math.min(180, (pullY / pullThreshold) * 180)}deg)` }}
              >↓</span>
            )}
          </div>
        </div>
      )}

      <Header
        jurisdiction={jurisdiction}
        isLoggedIn={auth.isLoggedIn}
        locked={auth.locked}
        npub={auth.npub}
        loginType={auth.loginType}
        profile={profile}
        nip05Verified={nip05Verified}
        quillState={quillState}
        onLogin={() => { setAuthModalTab(null); setAuthModal('choose') }}
        onCreateAccount={() => { setAuthModalTab('create'); setAuthModal('choose') }}
        onLogout={handleLogout}
        onUnlock={() => setAuthModal(unlockMode())}
        onJurisdictionClick={() => setTab('jurisdiction')}
        onAccountClick={() => setAccountOpen(true)}
      />
      <TabBar active={tab} onChange={handleTabChange} />

      {/* UX36a: quill toast — found/lost. Auto-dismisses after ~6s,
          user can also click ×. Positioned fixed at bottom-center so
          it doesn't fight the tab bar or the feed for attention. */}
      {quillToast && (
        <div className="quill-toast" role="status">
          <span className="quill-toast-icon" aria-hidden="true">🪶</span>
          <span className="quill-toast-text">
            {quillToast.type === 'found' ? (
              /* UX48 (revised): keep the mechanic mysterious. No
                 numeric target in user-facing copy. */
              <>You found a quill! Keep voting daily to hold it.</>
            ) : quillToast.canSave ? (
              /* UX47: today-is-grace copy — one vote restores the lost streak. */
              <>
                You&rsquo;ve lost your {quillToast.streakWas}-day streak,
                but it&rsquo;s not gone yet — <strong>vote today</strong> to save it.
              </>
            ) : (
              <>
                After {quillToast.daysHeld} day{quillToast.daysHeld === 1 ? '' : 's'},
                you&rsquo;ve lost your quill. Don&rsquo;t worry, you can find
                another one.
              </>
            )}
          </span>
          <button
            type="button"
            className="quill-toast-close"
            onClick={() => setQuillToast(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {streakCelebration && (
        <StreakCelebration
          days={streakCelebration.days}
          saved={streakCelebration.saved}
          daysHeld={streakCelebration.daysHeld}
          onClose={() => setStreakCelebration(null)}
        />
      )}

      {upgradePrompt && (
        <UpgradePrompt
          voteCount={upgradePrompt.voteCount}
          onDismiss={() => {
            snoozeUpgradePrompt(upgradePrompt.voteCount)
            setUpgradePrompt(null)
          }}
          onSave={() => {
            setUpgradePrompt(null)
            auth.linkWithGoogle?.()
          }}
        />
      )}

      <main className="app-main">
        {tab === 'feed' && (
          <>
            {!auth.pubkey && !jurisdiction && (
              <WelcomeCard
                onSignIn={() => setAuthModal(auth.locked ? unlockMode() : 'choose')}
                onSetLocation={() => setTab('jurisdiction')}
              />
            )}
            <div className="feed-disclaimer" role="note">
              {DISCLAIMER_TEXT}
            </div>
            {showJurisdictionBanner && (
              <div className="jurisdiction-banner" role="note">
                <span>Set your location to see polls that affect you.</span>
                <span style={{ display: 'inline-flex', gap: 8 }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setTab('jurisdiction')}
                  >
                    Set location
                  </button>
                  <button
                    type="button"
                    className="jurisdiction-banner-close"
                    onClick={dismissJurisdictionBanner}
                    aria-label="Dismiss for 24 hours"
                  >×</button>
                </span>
              </div>
            )}
            {postBanner && (
              <div
                className="post-banner"
                role="status"
                aria-live="polite"
              >
                <span>
                  ✅ Posted to {postBanner.accepted}/{postBanner.total} relays.
                  Your poll is at the top of the feed.
                </span>
                <button
                  type="button"
                  className="modal-close"
                  onClick={() => setPostBanner(null)}
                  aria-label="Dismiss"
                >×</button>
              </div>
            )}
            <FeedControls
              scope={scope}
              onScopeChange={setScope}
              query={query}
              onQueryChange={setQuery}
              activeTypes={activeTypes}
              onToggleType={toggleType}
              topic={topic}
              onTopicChange={setTopic}
              presentTopics={presentTopics}
              subscribedTopics={subscribedTopics}
              onToggleSubscribe={(t) => setSubscribedTopics(toggleTopicSub(auth.pubkey, t))}
              followedOnly={followedOnly}
              onToggleFollowedOnly={() => setFollowedOnly((v) => !v)}
              timeframe={timeframe}
              onTimeframeChange={setTimeframe}
            />

            {error && <div className="error-state">{error}</div>}

            {loading && !visibleItems.length && (
              <div className="loading-state">Loading polls from relays…</div>
            )}

            {!loading && !visibleItems.length && (
              <div className="empty-state">
                {query ? (
                  /* UX53: search-specific empty state with a quick clear
                     affordance. Keeps the active query visible so the
                     user can see what they typed. */
                  <>
                    No polls match <strong>&ldquo;{query}&rdquo;</strong>.{' '}
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => setQuery('')}
                    >
                      Clear search
                    </button>
                  </>
                ) : scope === 'mine' ? (
                  <>
                    No polls in your jurisdiction yet.{' '}
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => setScope('all')}
                    >
                      Switch to All
                    </button>{' '}
                    to see everything.
                  </>
                ) : (
                  'No polls match your filters.'
                )}
              </div>
            )}

            {/* UX46: return-visit catch-up strip. Only renders when
                prev lastSeen exists and new polls arrived since. */}
            <CatchUpCard
              count={catchUpDismissed ? 0 : catchUpCount}
              onDismiss={() => { setCatchUpDismissed(true); setCatchUpCount(0) }}
            />

            {/* UX67: What's moving in Congress — horizontal rail of
                the most recently-moved bills. Different signal from
                the heroes (editorial picks); this is "Congress just
                did these things." Sits above the hero so a visitor
                who lands here sees the real-time legislative pulse
                before anything else. Only renders when there's at
                least one recently-moved bill in the current feed. */}
            <MovingStrip
              items={itemsWithResidency}
              onOpen={(pollId) => {
                const el = document.querySelector(`[data-poll-id="${pollId}"]`)
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  el.classList.add('card-flash')
                  setTimeout(() => el.classList.remove('card-flash'), 1800)
                } else {
                  window.open(`/poll/${pollId}`, '_blank', 'noopener,noreferrer')
                }
              }}
            />

            {/* UX57: dual-hero PoTD. Pulse hero (always first when
                present) + bill-of-the-moment hero (conditional on
                server-side legislative score threshold). Badge label
                swaps based on pollOfTheDayKind so a legislative
                fallback doesn't mislabel as a "pulse" question. */}
            {pulseHero && (
              <div className="feed-hero">
                <div className="feed-hero-badge">
                  {pollOfTheDayKind === 'bill'
                    ? '⭐ Poll of the day'
                    : '⭐ Today’s pulse'}
                </div>
                <BillCard
                  key={pulseHero.id}
                  item={pulseHero}
                  tally={tallies[pulseHero.id]}
                  userVote={userVotes[pulseHero.id]}
                  onVote={handleVote}
                  onViewMetrics={handleViewMetrics}
                  onShareQuote={handleShareQuote}
                  isLoggedIn={auth.isLoggedIn}
                  activity={activityIndex[pulseHero.id]}
                  featured
                />
              </div>
            )}
            {billHero && (
              <div className="feed-hero feed-hero-bill">
                <div className="feed-hero-badge feed-hero-badge-bill">🔥 Bill of the moment</div>
                <BillCard
                  key={billHero.id}
                  item={billHero}
                  tally={tallies[billHero.id]}
                  userVote={userVotes[billHero.id]}
                  onVote={handleVote}
                  onViewMetrics={handleViewMetrics}
                  onShareQuote={handleShareQuote}
                  isLoggedIn={auth.isLoggedIn}
                  activity={activityIndex[billHero.id]}
                  featured
                />
              </div>
            )}
            {visibleItemsSansHero.map((item) =>
              item.kind === 'candidate' ? (
                <CandidateCard
                  key={item.id}
                  item={item}
                  tally={tallies[item.id]}
                  userVote={userVotes[item.id]}
                  onVote={handleVote}
                  onViewMetrics={handleViewMetrics}
                  onShareQuote={handleShareQuote}
                  isLoggedIn={auth.isLoggedIn}
                  activity={activityIndex[item.id]}
                />
              ) : (
                <BillCard
                  key={item.id}
                  item={item}
                  tally={tallies[item.id]}
                  userVote={userVotes[item.id]}
                  onVote={handleVote}
                  onViewMetrics={handleViewMetrics}
                  onShareQuote={handleShareQuote}
                  isLoggedIn={auth.isLoggedIn}
                />
              )
            )}
          </>
        )}

        {/* UX54: voting history pane. Reads userVotes + items; passing
            onOpenInFeed scrolls the card into view on the feed tab. */}
        {tab === 'history' && (
          <HistoryPane
            items={itemsWithResidency}
            userVotes={userVotes}
            isLoggedIn={auth.isLoggedIn}
            onOpenInFeed={(pollId) => {
              setTab('feed')
              // Give the feed render a frame to mount before scrolling.
              setTimeout(() => {
                const el = document.querySelector(`[data-poll-id="${pollId}"]`)
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                  el.classList.add('card-flash')
                  setTimeout(() => el.classList.remove('card-flash'), 1800)
                }
              }, 50)
            }}
          />
        )}

        {tab === 'analytics' && (
          <AnalyticsPane
            items={itemsWithResidency}
            tallies={tallies}
            voteIndex={voteIndex}
            jurisdiction={jurisdiction}
            userPubkey={auth.pubkey}
            initialPollId={analyticsPollId}
          />
        )}

        {tab === 'propose' && (
          <ProposePane
            isLoggedIn={auth.isLoggedIn}
            pubkey={auth.pubkey}
            sign={auth.sign}
            publish={nostr.publish}
            jurisdiction={jurisdiction}
            onSignIn={() => setAuthModal(auth.locked ? unlockMode() : 'choose')}
            onPosted={(signed, result) => {
              // Insert the just-signed event into the feed without waiting
              // for the relays to echo it back through the subscription.
              addLocalEvent(signed)
              // Persist so the poll survives reloads regardless of relay
              // behavior — some accept events but don't serve them back.
              rememberMyEvent(signed)
              // Make sure the new poll isn't filtered out by jurisdiction.
              setScope('all')
              setPostBanner({
                id: signed?.id,
                accepted: result?.accepted ?? 0,
                total: result?.total ?? 0,
                ts: Date.now(),
              })
              setTab('feed')
            }}
          />
        )}

        {tab === 'jurisdiction' && (
          <JurisdictionPane
            jurisdiction={jurisdiction}
            onChange={handleJurisdictionChange}
          />
        )}
      </main>

      <Footer />

      <SignInModal
        open={authModal !== null}
        mode={authModal || 'choose'}
        defaultTab={authModalTab}
        nostrAvailable={auth.nostrAvailable}
        error={auth.error}
        clearError={auth.clearError}
        onClose={() => { setAuthModal(null); setAuthModalTab(null) }}
        onLoginNip07={auth.loginNip07}
        onLoginWithGoogle={auth.loginWithGoogle}
        onImport={auth.importKey}
        onCreate={auth.createNew}
        onUnlock={auth.unlock}
        onLogout={handleLogout}
      />

      <AccountModal
        open={accountOpen}
        profile={profile}
        pubkey={auth.pubkey}
        npub={auth.npub}
        loginType={auth.loginType}
        locked={auth.locked}
        canLinkGoogle={auth.canLinkGoogle}
        nip05Verified={nip05Verified}
        canExportNsec={auth.canExportNsec}
        onExportNsec={auth.exportNsec}
        onLinkGoogle={auth.linkWithGoogle}
        onSign={auth.sign}
        onLogout={handleLogout}
        onUnlock={() => setAuthModal(unlockMode())}
        onUpdateProfile={updateProfile}
        onClose={() => setAccountOpen(false)}
      />
    </div>
  )
}

